import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Server, Users, Building2, Network, RefreshCw,
  LayoutDashboard, ChevronRight, ChevronDown, Radio,
  SlidersHorizontal, DatabaseZap, X, Menu, Wifi, BarChart2, ShieldCheck, KeyRound,
  Activity, ScrollText, History, FileText, Globe, Cable, Lock
} from 'lucide-react';

// roles yang TIDAK boleh melihat item ini (whitelist via "hideFor")
const navConfig = [
  { to: '/', icon: LayoutDashboard, label: 'Overview' },
  {
    label: 'Server', icon: Server, group: true, hideFor: ['operator', 'user'],
    children: [
      { to: '/radius-servers', icon: Server, label: 'Radius Server' },
      { to: '/worker-query', icon: DatabaseZap, label: 'Worker Query', hideFor: ['admin'] },
    ]
  },
  { to: '/tenants', icon: Building2, label: 'Tenant' },
  {
    label: 'Radius', icon: Users, group: true,
    children: [
      { to: '/users', icon: Users, label: 'Users' },
      { to: '/profiles', icon: SlidersHorizontal, label: 'Profile' },
      { to: '/nas', icon: Network, label: 'NAS Devices' },
      { to: '/online-users', icon: Wifi, label: 'Online Users' },
    ]
  },
  {
    label: 'VPN', icon: Lock, group: true,
    children: [
      { to: '/vpn/servers', icon: Globe, label: 'Instance Server', hideFor: ['operator', 'user'] },
      { to: '/vpn/wireguard', icon: Cable, label: 'Wireguard Peer' },
      { to: '/vpn/ipsec', icon: Cable, label: 'IP Sec Peer' },
    ]
  },
  { to: '/analytics', icon: BarChart2, label: 'Analytics' },
  { to: '/reports', icon: FileText, label: 'Reports' },
  {
    label: 'Sync & Logs', icon: RefreshCw, group: true,
    children: [
      { to: '/sync/status', icon: Activity, label: 'Sync Status', hideFor: ['user', 'operator'] },
      { to: '/sync/log', icon: ScrollText, label: 'Sync Log', hideFor: ['user', 'operator'] },
      { to: '/sync/auth-log', icon: History, label: 'Auth Log' },
      { to: '/sync/change-log', icon: History, label: 'Change Log' },
    ]
  },
  {
    label: 'Settings', icon: ShieldCheck, group: true,
    children: [
      { to: '/admin-users', icon: ShieldCheck, label: 'Admin Users' },
      { to: '/api-tokens', icon: KeyRound, label: 'API Token' },
    ]
  },
];

const visibleChildren = (item, role) => (item.children || []).filter(c => !c.hideFor || !c.hideFor.includes(role));
const visibleNav = (role) => navConfig
  .filter(item => !item.hideFor || !item.hideFor.includes(role))
  .map(item => item.group ? { ...item, children: visibleChildren(item, role) } : item)
  .filter(item => !item.group || item.children.length > 0);

/* Link nav reusable dengan hover animasi (dipakai buat item single, submenu accordion, & submenu flyout).
   Kalau flyout=true (item single tanpa children, saat collapsed), hover-nya munculin bubble label
   di samping via portal ke document.body -- sama persis gayanya kayak submenu group. */
function SidebarLink({ to, end, icon: IconComp, label, onClick, padding, justify = 'flex-start', showLabel = true, iconSize = 14, flyout = false }) {
  const [hovered, setHovered] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 64 });
  const linkRef = useRef(null);
  const closeTimer = useRef(null);

  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const handleEnter = () => {
    setHovered(true);
    if (!flyout) return;
    cancelClose();
    const rect = linkRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.top + rect.height / 2, left: rect.right + 4 });
  };
  const handleLeave = () => {
    if (!flyout) { setHovered(false); return; }
    closeTimer.current = setTimeout(() => setHovered(false), 100);
  };

  return (
    <>
      <NavLink ref={linkRef} to={to} end={end} onClick={onClick}
        onMouseEnter={handleEnter} onMouseLeave={handleLeave}
        style={({ isActive }) => ({
          display: 'flex', alignItems: 'center', gap: 8,
          padding, justifyContent: justify,
          borderRadius: 'var(--radius)', marginBottom: 2,
          color: isActive ? 'var(--accent-blue)' : hovered ? 'var(--text-primary)' : 'var(--text-secondary)',
          background: isActive ? 'rgba(59,130,246,0.1)' : hovered ? 'var(--bg-card-hover)' : 'transparent',
          border: `1px solid ${isActive ? 'rgba(59,130,246,0.2)' : hovered ? 'var(--border)' : 'transparent'}`,
          fontSize: 13, fontWeight: isActive ? 600 : 400,
          textDecoration: 'none', whiteSpace: 'nowrap', overflow: 'hidden',
          transform: hovered && !isActive ? 'translateX(2px)' : 'translateX(0)',
          transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.15s ease',
        })}>
        {({ isActive }) => (
          <>
            <IconComp size={iconSize} style={{
              flexShrink: 0,
              color: isActive ? 'var(--accent-blue)' : hovered ? 'var(--text-primary)' : 'var(--text-muted)',
              transform: hovered ? 'scale(1.12)' : 'scale(1)',
              transition: 'transform 0.15s ease, color 0.15s ease',
            }} />
            {showLabel && label}
          </>
        )}
      </NavLink>

      {flyout && hovered && createPortal(
        <div onMouseEnter={cancelClose} onMouseLeave={handleLeave}
          style={{
            position: 'fixed', top: pos.top, left: pos.left, transform: 'translateY(-50%)',
            background: 'var(--bg-secondary)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
            padding: '7px 12px', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)',
            whiteSpace: 'nowrap', zIndex: 9998, animation: 'fadeIn .12s ease-out',
            pointerEvents: 'none',
          }}>
          {label}
        </div>,
        document.body
      )}
    </>
  );
}

function GroupItem({ item, collapsed, onNavClick, isFirst }) {
  const [open, setOpen] = useState(true);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [btnHovered, setBtnHovered] = useState(false);
  const [flyoutPos, setFlyoutPos] = useState({ top: 0, left: 64 });
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);
  const Icon = item.icon;

  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const handleEnter = () => {
    setBtnHovered(true);
    if (!collapsed) return;
    cancelClose();
    const rect = wrapRef.current?.getBoundingClientRect();
    if (rect) setFlyoutPos({ top: rect.top, left: rect.right + 4 });
    setFlyoutOpen(true);
  };
  const handleLeave = () => {
    setBtnHovered(false);
    if (!collapsed) return;
    // sedikit delay biar mouse sempat pindah dari icon ke flyout tanpa flicker
    closeTimer.current = setTimeout(() => setFlyoutOpen(false), 120);
  };

  return (
    <div ref={wrapRef}
      style={{ marginTop: isFirst ? 0 : 14, paddingTop: isFirst ? 0 : 10, borderTop: isFirst || collapsed ? 'none' : '1px solid var(--border)', position: 'relative' }}
      onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button onClick={() => !collapsed && setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between',
          width: '100%', padding: collapsed ? '10px 16px' : '4px 12px',
          background: btnHovered ? 'var(--bg-card-hover)' : 'none',
          border: 'none', cursor: collapsed ? 'default' : 'pointer', borderRadius: 'var(--radius)',
          color: btnHovered ? 'var(--text-secondary)' : 'var(--text-muted)', fontSize: 10.5, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6,
          transition: 'background 0.15s ease, color 0.15s ease',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon size={13} style={{ flexShrink: 0, opacity: 0.7, transform: btnHovered ? 'scale(1.1)' : 'scale(1)', transition: 'transform 0.15s ease' }} />
          {!collapsed && item.label}
        </div>
        {!collapsed && <ChevronDown size={12} style={{ transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform 0.2s', flexShrink: 0, opacity: 0.7 }} />}
      </button>

      {/* Expanded: submenu inline (accordion) */}
      {!collapsed && open && (
        <div style={{ paddingLeft: 10 }}>
          {item.children.map(child => (
            <SidebarLink key={child.to} to={child.to} icon={child.icon} label={child.label}
              onClick={onNavClick} padding="8px 12px" />
          ))}
        </div>
      )}

      {/* Collapsed: submenu muncul sebagai flyout di samping, saat hover (portal ke body biar gak kena overflow/stacking context manapun) */}
      {collapsed && flyoutOpen && createPortal(
        <div onMouseEnter={cancelClose} onMouseLeave={handleLeave}
          style={{
            position: 'fixed', top: flyoutPos.top, left: flyoutPos.left,
            background: 'var(--bg-secondary)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
            minWidth: 190, padding: 6, zIndex: 9998,
            animation: 'fadeIn .12s ease-out',
          }}>
          <div style={{
            padding: '6px 10px 8px', marginBottom: 4,
            borderBottom: '1px solid var(--border)',
            fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.1em', color: 'var(--text-muted)',
          }}>
            {item.label}
          </div>
          {item.children.map(child => (
            <SidebarLink key={child.to} to={child.to} icon={child.icon} label={child.label}
              onClick={() => { setFlyoutOpen(false); onNavClick?.(); }} padding="8px 10px" />
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

function SidebarContent({ collapsed, onToggle, onNavClick }) {
  const { user } = useAuth();
  const nav = visibleNav(user?.role);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <div style={{
        padding: collapsed ? '20px 16px' : '20px',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 64,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 8, flexShrink: 0,
            background: 'linear-gradient(135deg, var(--accent-blue), var(--accent-cyan))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Radio size={14} color="#fff" />
          </div>
          {!collapsed && (
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>RADIUS</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.1em' }}>MGMT DASHBOARD</div>
            </div>
          )}
        </div>
        {/* Close button on mobile (X), collapse on desktop */}
        <button onClick={onToggle} style={{
          background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer',
          padding: 4, display: 'flex', alignItems: 'center',
        }}>
          {collapsed ? null : <X size={16} />}
        </button>
      </div>

      {/* Nav */}
      <nav style={{ padding: '12px 8px', flex: 1, overflowY: 'auto' }}>
        {nav.map((item, i) =>
          item.group ? (
            <GroupItem key={i} item={item} collapsed={collapsed} onNavClick={onNavClick} isFirst={i === 0} />
          ) : (
            <SidebarLink key={item.to} to={item.to} end={item.to === '/'} icon={item.icon} label={item.label}
              onClick={onNavClick}
              padding={collapsed ? '10px 16px' : '9px 12px'}
              justify={collapsed ? 'center' : 'flex-start'}
              showLabel={!collapsed} iconSize={16} flyout={collapsed} />
          )
        )}
      </nav>

      {/* Collapse toggle (desktop only) */}
      {!onNavClick && (
        <div style={{ padding: '12px 8px', borderTop: '1px solid var(--border)' }}>
          <button onClick={onToggle} style={{
            display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-end',
            width: '100%', background: 'none', border: 'none',
            color: 'var(--text-muted)', padding: '8px', borderRadius: 'var(--radius)',
            cursor: 'pointer', gap: 6, fontSize: 12,
          }}>
            {!collapsed && <span>Collapse</span>}
            <ChevronRight size={14} style={{ transform: collapsed ? 'none' : 'rotate(180deg)', transition: 'transform 0.2s' }} />
          </button>
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }) {
  return (
    <>
      {/* Desktop sidebar */}
      <aside style={{
        width: collapsed ? 60 : 220, minWidth: collapsed ? 60 : 220,
        background: 'var(--bg-secondary)', borderRight: '1px solid var(--border)',
        transition: 'width 0.2s ease, min-width 0.2s ease',
        overflow: 'hidden', height: '100vh', position: 'sticky', top: 0,
        display: 'var(--sidebar-desktop-display, flex)',
        flexDirection: 'column',
      }} className="sidebar-desktop">
        <SidebarContent collapsed={collapsed} onToggle={onToggle} />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          display: 'flex',
        }}>
          {/* backdrop */}
          <div onClick={onMobileClose} style={{
            position: 'absolute', inset: 0,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)',
          }} />
          {/* drawer */}
          <aside style={{
            position: 'relative', zIndex: 1,
            width: 240, background: 'var(--bg-secondary)',
            borderRight: '1px solid var(--border)',
            height: '100%', overflowY: 'auto',
          }}>
            <SidebarContent collapsed={false} onToggle={onMobileClose} onNavClick={onMobileClose} />
          </aside>
        </div>
      )}
    </>
  );
}